/* eslint-disable react-refresh/only-export-components --
   This file is the application's route table. It deliberately
   co-exports `router` (a non-component) alongside lazy() page
   constants so the routes tree lives in one place. Splitting
   would require a routes-only re-export shim and break Vite's
   lazy-import chunking. */
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ToastContainer } from '@/components/feedback/ToastContainer';
import { ProtectedRoute } from '@/components/layout/ProtectedRoute';
import { MonitoringRouteEffects } from '@/lib/monitoring/MonitoringRouteEffects';
import { Sentry } from '@/lib/monitoring/sentry';
import { isReleaseFeatureEnabled, type ReleaseFlag } from '@/lib/release-flags';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage';
import { SetPasswordPage } from '@/features/auth/pages/SetPasswordPage';
import { NotFoundPage } from '@/features/auth/pages/NotFoundPage';
import { SsoCallbackPage } from '@/features/auth/pages/SsoCallbackPage';
import { MfaEnrollPage } from '@/features/auth/pages/MfaEnrollPage';
import { MfaVerifyPage } from '@/features/auth/pages/MfaVerifyPage';
import { RegisterPage } from '@/features/auth/pages/RegisterPage';

// Lazy-loaded feature pages for code splitting
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const PropertyDashboardPage = lazy(() => import('@/features/dashboard/pages/PropertyDashboardPage').then((m) => ({ default: m.PropertyDashboardPage })));
const OpportunitiesDashboardPage = lazy(() => import('@/features/dashboard/pages/OpportunitiesDashboardPage').then((m) => ({ default: m.OpportunitiesDashboardPage })));
// PR #49 — DASH-W1-4 BASE redirect + Limited denial dispatcher.
// Loaded eagerly (not lazy) so the landing decision doesn't show a
// spinner before the inevitable Navigate fires.
import { LandingRedirect } from '@/features/dashboard/components/LandingRedirect';
const PropertiesPage = lazy(() => import('@/features/properties/pages/PropertiesPage').then((m) => ({ default: m.PropertiesPage })));
const PropertyDetailPage = lazy(() => import('@/features/properties/pages/PropertyDetailPage').then((m) => ({ default: m.PropertyDetailPage })));
const MeasurementReportWindowPage = lazy(() =>
  import('@/features/properties/pages/MeasurementReportWindowPage').then((m) => ({
    default: m.MeasurementReportWindowPage,
  })),
);
const PropertyFormPage = lazy(() => import('@/features/properties/pages/PropertyFormPage').then((m) => ({ default: m.PropertyFormPage })));
const OrderListPage = lazy(() => import('@/features/orders/pages/OrderListPage').then((m) => ({ default: m.OrderListPage })));
const OrderDetailPage = lazy(() => import('@/features/orders/pages/OrderDetailPage').then((m) => ({ default: m.OrderDetailPage })));
const OrderCreatePage = lazy(() => import('@/features/orders/pages/OrderCreatePage').then((m) => ({ default: m.OrderCreatePage })));
import {
  WorkOrderCreateLegacyRedirect,
  WorkOrderDetailLegacyRedirect,
} from '@/features/orders/components/OrderLegacyRouteRedirects';
const SearchPage = lazy(() => import('@/features/search/pages/SearchPage').then((m) => ({ default: m.SearchPage })));
const AlertsPage = lazy(() => import('@/features/alerts/pages/AlertsPage').then((m) => ({ default: m.AlertsPage })));
const MessagesPage = lazy(() => import('@/features/messages/pages/MessagesPage').then((m) => ({ default: m.MessagesPage })));
const AdminCompaniesPage = lazy(() => import('@/features/admin/pages/AdminCompaniesPage').then((m) => ({ default: m.AdminCompaniesPage })));
const AdminCompanyDetailPage = lazy(() => import('@/features/admin/pages/AdminCompanyDetailPage').then((m) => ({ default: m.AdminCompanyDetailPage })));
// AdminUsersPage replaced by /teams?tab=all (PRD 06-Teams).
// Detail page is still mounted at /admin/users/:userId.
const AdminUserDetailPage = lazy(() => import('@/features/admin/pages/AdminUserDetailPage').then((m) => ({ default: m.AdminUserDetailPage })));
// AdminPropertyMappingPage replaced by /teams (PRD 06-Teams).
const AdminSettingsPage = lazy(() => import('@/features/admin/pages/AdminSettingsPage').then((m) => ({ default: m.AdminSettingsPage })));
const SettingsLayout = lazy(() => import('@/features/admin/settings/pages/SettingsLayout').then((m) => ({ default: m.SettingsLayout })));
const SystemSettingsHubPage = lazy(() => import('@/features/admin/settings/pages/SystemSettingsHubPage').then((m) => ({ default: m.SystemSettingsHubPage })));
const NotificationsEnginePage = lazy(() => import('@/features/admin/settings/pages/NotificationsEnginePage').then((m) => ({ default: m.NotificationsEnginePage })));
const EmailCcPolicyPage = lazy(() => import('@/features/admin/settings/pages/EmailCcPolicyPage').then((m) => ({ default: m.EmailCcPolicyPage })));
const AutoFixSettingsPage = lazy(() => import('@/features/admin/settings/pages/AutoFixSettingsPage').then((m) => ({ default: m.AutoFixSettingsPage })));
const NotificationBroadcastPage = lazy(() => import('@/features/admin/settings/pages/NotificationBroadcastPage').then((m) => ({ default: m.NotificationBroadcastPage })));

const AuditTrailPage = lazy(() => import('@/features/admin/pages/AuditTrailPage').then((m) => ({ default: m.AuditTrailPage })));
const AdminPricingPage = lazy(() => import('@/features/admin/pages/AdminPricingPage').then((m) => ({ default: m.AdminPricingPage })));
const ReportsPage = lazy(() => import('@/features/reports/pages/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const TeamsPage = lazy(() => import('@/features/teams/pages/TeamsPage').then((m) => ({ default: m.TeamsPage })));
const SupportPage = lazy(() => import('@/features/support/pages/SupportPage').then((m) => ({ default: m.SupportPage })));
const SupportHcRedirectPage = lazy(() =>
  import('@/features/support/pages/SupportHcRedirectPage').then((m) => ({
    default: m.SupportHcRedirectPage,
  })),
);
const ReportsHubPage = lazy(() => import('@/features/reports/pages/ReportsHubPage').then((m) => ({ default: m.ReportsHubPage })));
const ReportDetailPage = lazy(() => import('@/features/reports/pages/ReportDetailPage').then((m) => ({ default: m.ReportDetailPage })));
const ReportComparePage = lazy(() => import('@/features/reports/pages/ReportComparePage').then((m) => ({ default: m.ReportComparePage })));
const SABReportPage = lazy(() => import('@/features/reports/pages/SABReportPage').then((m) => ({ default: m.SABReportPage })));
const ReportSubmissionPage = lazy(() => import('@/features/reports/pages/ReportSubmissionPage').then((m) => ({ default: m.ReportSubmissionPage })));
const ReviewQueuePage = lazy(() => import('@/features/reports/pages/ReviewQueuePage').then((m) => ({ default: m.ReviewQueuePage })));
const PdfViewerPage = lazy(() => import('@/features/pdf-viewer/pages/PdfViewerPage').then((m) => ({ default: m.PdfViewerPage })));
const ReportAnnotateRoute = lazy(() => import('@/features/pdf-viewer/pages/ReportAnnotateRoute').then((m) => ({ default: m.ReportAnnotateRoute })));
const DxfViewerPage = lazy(() => import('@/features/dxf-viewer/pages/DxfViewerPage').then((m) => ({ default: m.DxfViewerPage })));
const ImageViewerPage = lazy(() => import('@/features/image-viewer/pages/ImageViewerPage').then((m) => ({ default: m.ImageViewerPage })));
const DrawingVaultPage = lazy(() => import('@/features/drawing-vault/pages/DrawingVaultPage').then((m) => ({ default: m.DrawingVaultPage })));
const PropertyDrawingsLegacyRedirect = lazy(() =>
  import('@/features/drawing-vault/components/PropertyDrawingsLegacyRedirect').then((m) => ({
    default: m.PropertyDrawingsLegacyRedirect,
  })),
);
const VaultLandingPage = lazy(() => import('@/features/drawing-vault/pages/VaultLandingPage').then((m) => ({ default: m.VaultLandingPage })));
const VaultReportsPage = lazy(() => import('@/features/drawing-vault/pages/VaultReportsPage').then((m) => ({ default: m.VaultReportsPage })));
const ProfilePage = lazy(() => import('@/features/profile/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })));
const AccountHubPage = lazy(() => import('@/features/account/pages/AccountHubPage').then((m) => ({ default: m.AccountHubPage })));
const OrganizationPage = lazy(() => import('@/features/account/pages/OrganizationPage').then((m) => ({ default: m.OrganizationPage })));
const ActiveSessionsPage = lazy(() => import('@/features/account/pages/ActiveSessionsPage').then((m) => ({ default: m.ActiveSessionsPage })));
const MfaManagementPage = lazy(() => import('@/features/account/pages/MfaManagementPage').then((m) => ({ default: m.MfaManagementPage })));
const ApiKeysPage = lazy(() => import('@/features/account/pages/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })));
const IntegrationsHubPage = lazy(() => import('@/features/integrations/pages/IntegrationsHubPage').then((m) => ({ default: m.IntegrationsHubPage })));
const IntegrationPage = lazy(() => import('@/features/integrations/pages/IntegrationPage').then((m) => ({ default: m.IntegrationPage })));
const AdminDomainsPage = lazy(() => import('@/features/admin/pages/AdminDomainsPage').then((m) => ({ default: m.AdminDomainsPage })));
const AdminSubscriptionsPage = lazy(() => import('@/features/admin/pages/AdminSubscriptionsPage').then((m) => ({ default: m.AdminSubscriptionsPage })));
const AdminEmailTemplatesPage = lazy(() => import('@/features/admin/pages/AdminEmailTemplatesPage').then((m) => ({ default: m.AdminEmailTemplatesPage })));
const NotificationTemplatesListPage = lazy(() => import('@/features/admin/settings/pages/NotificationTemplatesListPage').then((m) => ({ default: m.NotificationTemplatesListPage })));
// AdminInvitesPage replaced by /teams?tab=pending (PRD 06-Teams).
const AdminWidgetPermissionsPage = lazy(() => import('@/features/admin/pages/AdminWidgetPermissionsPage').then((m) => ({ default: m.AdminWidgetPermissionsPage })));
const AdminSubscriptionLimitsPage = lazy(() => import('@/features/admin/pages/AdminSubscriptionLimitsPage').then((m) => ({ default: m.AdminSubscriptionLimitsPage })));
const AdminPropertyImportPage = lazy(() => import('@/features/admin/pages/AdminPropertyImportPage').then((m) => ({ default: m.AdminPropertyImportPage })));
const AdminCompanyFlagsPage = lazy(() => import('@/features/admin/pages/AdminCompanyFlagsPage').then((m) => ({ default: m.AdminCompanyFlagsPage })));
const AdminTooltipsPage = lazy(() => import('@/features/admin/pages/AdminTooltipsPage').then((m) => ({ default: m.AdminTooltipsPage })));
const AdminDrawingVaultPage = lazy(() => import('@/features/admin/drawing-vault/pages/AdminDrawingVaultPage').then((m) => ({ default: m.AdminDrawingVaultPage })));
const RoadmapPage = lazy(() => import('@/features/roadmap/pages/RoadmapPage').then((m) => ({ default: m.RoadmapPage })));
const MyWorkPage = lazy(() => import('@/features/roadmap/pages/MyWorkPage').then((m) => ({ default: m.MyWorkPage })));
const FeatureRequestsPage = lazy(() => import('@/features/roadmap/pages/FeatureRequestsPage').then((m) => ({ default: m.FeatureRequestsPage })));
const FeatureRequestDetailPage = lazy(() => import('@/features/roadmap/pages/FeatureRequestDetailPage').then((m) => ({ default: m.FeatureRequestDetailPage })));
const RoadmapActivityPage = lazy(() => import('@/features/roadmap/pages/RoadmapActivityPage').then((m) => ({ default: m.RoadmapActivityPage })));
const RoadmapTaxonomyPage = lazy(() => import('@/features/roadmap/pages/RoadmapTaxonomyPage').then((m) => ({ default: m.RoadmapTaxonomyPage })));
const SprintsPage = lazy(() => import('@/features/roadmap/pages/SprintsPage').then((m) => ({ default: m.SprintsPage })));
const SprintDetailPage = lazy(() => import('@/features/roadmap/pages/SprintDetailPage').then((m) => ({ default: m.SprintDetailPage })));
const SupportDashboardPage = lazy(() => import('@/features/roadmap/pages/SupportDashboardPage').then((m) => ({ default: m.SupportDashboardPage })));
const AlertPreferencesPage = lazy(() => import('@/features/alerts/pages/AlertPreferencesPage').then((m) => ({ default: m.AlertPreferencesPage })));
const NotificationPreferencesPage = lazy(() =>
  import('@/features/notifications/pages/NotificationPreferencesPage').then((m) => ({
    default: m.NotificationPreferencesPage,
  })),
);

// Design-system showcase page — reachable at /v2/dev/design-system
// while signed in. Renders every primitive in `components/ui/`
// inside a realistic dashboard layout. Use as the visual reference
// when adopting shadcn primitives in feature PRs. See:
// .cursor/rules/component-visual.mdc §1.
const DesignSystemPage = lazy(() => import('@/features/_dev/pages/DesignSystemPage').then((m) => ({ default: m.DesignSystemPage })));

// Portfolio migration-accuracy dashboard (SSI-only). Live Legacy vs New parity
// across every report — for verifying the CAD-engine port at executive scale.
const MigrationAccuracyPage = lazy(() => import('@/features/reports/pages/MigrationAccuracyPage').then((m) => ({ default: m.MigrationAccuracyPage })));

/** Loading spinner shown while lazy chunks load */
function PageLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-border border-t-muted-foreground" />
    </div>
  );
}

/** Wrap lazy page in Suspense */
function lazy$(element: React.ReactNode) {
  return <Suspense fallback={<PageLoader />}>{element}</Suspense>;
}

/** Root layout — keeps ToastContainer inside the router for route-aware positioning. */
function AppRoot() {
  return (
    <>
      <MonitoringRouteEffects />
      <Outlet />
      <ToastContainer />
    </>
  );
}

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

/**
 * Render a release-gated page only when its flag is enabled; otherwise show
 * the 404 page so hidden V3-only features can't be reached via bookmark or
 * direct URL while they're flagged off.
 */
function flagged(flag: ReleaseFlag, element: React.ReactNode): React.ReactNode {
  return isReleaseFeatureEnabled(flag) ? element : <NotFoundPage />;
}

/** Wrap a page in the protected route + app shell layout */
function protectedPage(element: React.ReactNode) {
  return (
    <ProtectedRoute>
      {element}
    </ProtectedRoute>
  );
}

export const router = sentryCreateBrowserRouter(
  [
    {
      element: <AppRoot />,
      children: [
    // Public routes
    { path: '/login', element: <LoginPage /> },
    { path: '/register', element: <RegisterPage /> },
    { path: '/forgot-password', element: <ForgotPasswordPage /> },
    { path: '/reset-password', element: <ResetPasswordPage /> },
    // Migrated reset-required users (Phase 7) land here from the Supabase
    // activation/recovery email to set their first V3 password.
    { path: '/set-password', element: <SetPasswordPage /> },
    { path: '/mfa/verify', element: <MfaVerifyPage /> },
    { path: '/auth/sso/callback', element: <SsoCallbackPage /> },

    // Authenticated routes (inside AppShell)
    {
      element: protectedPage(<AppShell />),
      children: [
        { index: true, element: <LandingRedirect /> },
        { path: '/dashboard', element: lazy$(<DashboardPage />) },
        { path: '/property-dashboard/:buildingId', element: lazy$(<PropertyDashboardPage />) },
        { path: '/opportunities', element: flagged('opportunities', lazy$(<OpportunitiesDashboardPage />)) },
        { path: '/properties', element: lazy$(<PropertiesPage />) },
        { path: '/properties/new', element: lazy$(<PropertyFormPage />) },
        { path: '/properties/:buildingId', element: lazy$(<PropertyDetailPage />) },
        // FR-300: report panel only. AppShell hides sidebar and header for this path.
        {
          path: '/properties/:buildingId/reports/:reportId/window',
          element: lazy$(<MeasurementReportWindowPage />),
        },
        { path: '/properties/:buildingId/edit', element: lazy$(<PropertyFormPage />) },
        // Legacy standalone vault URL — canonical UI is the property Drawings tab.
        { path: '/properties/:buildingId/drawings', element: lazy$(<PropertyDrawingsLegacyRedirect />) },
        { path: '/drawing-vault', element: lazy$(<VaultLandingPage />) },
        { path: '/drawing-vault/:buildingId', element: lazy$(<DrawingVaultPage />) },
        { path: '/drawings/reports', element: flagged('vault-reports', lazy$(<VaultReportsPage />)) },
        // PRD 04-Reports §4.1: the cross-property Reports hub owns
        // `/reports`. The legacy per-building measurement queue keeps
        // its UI but moves to `/reports/measurement` so SSI users can
        // Legacy measurement queue URL redirects into the hub tab.
        { path: '/reports', element: lazy$(<ReportsHubPage />) },
        { path: '/reports/measurement', element: lazy$(<ReportsPage />) },
        { path: '/reports/reviews', element: flagged('admin-v3-extras', lazy$(<ReviewQueuePage />)) },
        { path: '/reports/:reportId', element: lazy$(<ReportAnnotateRoute />) },
        { path: '/reports/:reportId/details', element: lazy$(<ReportDetailPage />) },
        { path: '/reports/:reportId/sab', element: lazy$(<SABReportPage />) },
        { path: '/reports/:reportId/submit', element: lazy$(<ReportSubmissionPage />) },
        { path: '/reports/:reportId/compare/:compareId', element: lazy$(<ReportComparePage />) },
        { path: '/reports/:reportId/annotate', element: <Navigate to="../" replace /> },
        // PRD 04-Reports §4.3 lives in the hub; the legacy standalone
        // page is preserved at /analytics/value-opportunities but it
        // also redirects clients to the hub tab. The page itself is no
        // longer linked from the sidebar — kept for back-compat only.
        { path: '/analytics/value-opportunities', element: <Navigate to="/reports?tab=value-opportunity" replace /> },
        { path: '/orders', element: lazy$(<OrderListPage />) },
        { path: '/orders/new', element: lazy$(<OrderCreatePage />) },
        { path: '/orders/:id', element: lazy$(<OrderDetailPage />) },
        { path: '/work-orders/new', element: <WorkOrderCreateLegacyRedirect /> },
        { path: '/work-orders/:id', element: <WorkOrderDetailLegacyRedirect /> },
        { path: '/search', element: lazy$(<SearchPage />) },
        { path: '/alerts', element: lazy$(<AlertsPage />) },
        { path: '/alerts/preferences', element: lazy$(<AlertPreferencesPage />) },
        { path: '/notifications/preferences', element: lazy$(<NotificationPreferencesPage />) },
        { path: '/messages', element: flagged('messages', lazy$(<MessagesPage />)) },

        // PRD 06-Teams — Teams module (top-level nav).
        // The four tabs are driven by `?tab=` on the same page so deep
        // links and the legacy admin redirects below work cleanly.
        { path: '/teams', element: lazy$(<TeamsPage />) },

        // PRD 09-Support — Support module (top-level nav).
        // All roles see the nav entry. When support-zendesk is ON the
        // sidebar opens Zendesk SSO instead; stale /support bookmarks
        // redirect to Properties (QA SUPPORT deep-link — no dead 404).
        // When OFF the native SupportPage renders as today.
        {
          path: '/support',
          element: isReleaseFeatureEnabled('support-zendesk')
            ? <Navigate to="/properties" replace />
            : lazy$(<SupportPage />),
        },
        // DEF-417: share-email Zendesk article gateway (always mounted; needs auth session).
        { path: '/support/hc', element: lazy$(<SupportHcRedirectPage />) },
        { path: '/pdf-viewer', element: lazy$(<PdfViewerPage />) },
        { path: '/dxf-viewer', element: lazy$(<DxfViewerPage />) },
        { path: '/image-viewer', element: lazy$(<ImageViewerPage />) },
        { path: '/profile', element: lazy$(<ProfilePage />) },
        { path: '/account', element: lazy$(<AccountHubPage />) },
        { path: '/account/profile', element: <Navigate to="/profile" replace /> },
        { path: '/account/password', element: <Navigate to="/profile" replace /> },
        { path: '/account/mfa', element: lazy$(<MfaManagementPage />) },
        { path: '/account/organization', element: lazy$(<OrganizationPage />) },
        { path: '/account/sessions', element: lazy$(<ActiveSessionsPage />) },
        { path: '/account/api-keys', element: lazy$(<ApiKeysPage />) },
        { path: '/account/integrations', element: lazy$(<IntegrationsHubPage />) },
        { path: '/account/integrations/:systemId', element: lazy$(<IntegrationPage />) },

        // Admin routes
        { path: '/admin', element: <Navigate to="/admin/companies" replace /> },
        { path: '/admin/companies', element: lazy$(<AdminCompaniesPage />) },
        { path: '/admin/companies/:companyId', element: lazy$(<AdminCompanyDetailPage />) },
        // PRD 06-Teams replaces three admin entries. Old bookmarks
        // redirect to the equivalent Teams tab.
        { path: '/admin/users', element: <Navigate to="/teams?tab=all" replace /> },
        { path: '/admin/users/:userId', element: lazy$(<AdminUserDetailPage />) },
        { path: '/admin/property-mapping', element: <Navigate to="/teams?tab=all" replace /> },
        // System Settings — big-card hub (Notifications | OnBoarding).
        {
          path: '/admin/settings',
          element: lazy$(<SettingsLayout />),
          children: [
            { index: true, element: lazy$(<SystemSettingsHubPage />) },
            {
              path: 'notifications',
              element: lazy$(<NotificationsEnginePage />),
              children: [
                { index: true, element: lazy$(<NotificationTemplatesListPage />) },
                { path: 'email-cc', element: lazy$(<EmailCcPolicyPage />) },
                { path: 'broadcast', element: lazy$(<NotificationBroadcastPage />) },
              ],
            },
            { path: 'onboarding', element: lazy$(<AdminTooltipsPage />) },
            { path: 'auto-fix', element: lazy$(<AutoFixSettingsPage />) },
            { path: 'raw', element: lazy$(<AdminSettingsPage />) },
          ],
        },
        { path: '/admin/settings/notification-templates', element: <Navigate to="/admin/settings/notifications" replace /> },
        { path: '/admin/settings/email-cc', element: <Navigate to="/admin/settings/notifications/email-cc" replace /> },
        { path: '/admin/settings/order-status-messages', element: <Navigate to="/admin/settings/notifications" replace /> },
        { path: '/admin/tooltips', element: <Navigate to="/admin/settings/onboarding" replace /> },
        { path: '/admin/audit-trail', element: lazy$(<AuditTrailPage />) },
        { path: '/admin/pricing', element: lazy$(<AdminPricingPage />) },
        { path: '/admin/domains', element: lazy$(<AdminDomainsPage />) },
        { path: '/admin/subscriptions', element: lazy$(<AdminSubscriptionsPage />) },
        { path: '/admin/email-templates', element: lazy$(<AdminEmailTemplatesPage />) },
        { path: '/admin/invites', element: <Navigate to="/teams?tab=pending" replace /> },
        { path: '/admin/widget-permissions', element: flagged('admin-v3-extras', lazy$(<AdminWidgetPermissionsPage />)) },
        { path: '/admin/subscription-limits', element: flagged('admin-v3-extras', lazy$(<AdminSubscriptionLimitsPage />)) },
        { path: '/admin/property-imports', element: flagged('admin-v3-extras', lazy$(<AdminPropertyImportPage />)) },
        { path: '/admin/company-flags', element: lazy$(<AdminCompanyFlagsPage />) },
        { path: '/admin/drawing-vault', element: lazy$(<AdminDrawingVaultPage />) },
        // SSI-internal roadmap + feature-request backlog (SSI_ONLY in ROUTE_PERMISSIONS).
        { path: '/admin/roadmap', element: lazy$(<RoadmapPage />) },
        { path: '/admin/roadmap/my-work', element: lazy$(<MyWorkPage />) },
        { path: '/admin/roadmap/activity', element: lazy$(<RoadmapActivityPage />) },
        { path: '/admin/roadmap/taxonomy', element: lazy$(<RoadmapTaxonomyPage />) },
        {
          path: '/admin/roadmap/mine',
          element: <Navigate to="/admin/roadmap/activity?kind=mentions" replace />,
        },
        { path: '/admin/roadmap/sprints', element: lazy$(<SprintsPage />) },
        { path: '/admin/roadmap/sprints/:id', element: lazy$(<SprintDetailPage />) },
        // Private product-ops dashboard. No sidebar or section link — URL only.
        { path: '/admin/roadmap/ops', element: lazy$(<SupportDashboardPage />) },
        { path: '/admin/roadmap/requests', element: lazy$(<FeatureRequestsPage />) },
        { path: '/admin/roadmap/requests/:id', element: lazy$(<FeatureRequestDetailPage />) },
        { path: '/mfa/enroll', element: lazy$(<MfaEnrollPage />) },

        // Design-system showcase. Lazy-loaded; the rest of the app
        // pays nothing for it to exist.
        { path: '/dev/design-system', element: lazy$(<DesignSystemPage />) },
        { path: '/dev/migration-accuracy', element: lazy$(<MigrationAccuracyPage />) },

        // DEF-130: the catch-all lives INSIDE the protected tree (not as a
        // top-level sibling) so an unmapped path — e.g. a legacy/guessed URL
        // like `/admin/companies/:id/users`, which has no dedicated v3 route —
        // still runs through ProtectedRoute's role check first. A path whose
        // prefix the caller's role can't reach (per `canAccessRoute`) redirects
        // to that role's landing page instead of quietly rendering NotFoundPage
        // with no data. A path that's merely unmapped (role has no restriction
        // on the prefix) still falls through to the 404 page below.
        { path: '*', element: <NotFoundPage /> },
      ],
    },
      ],
    },
  ],
  {
    basename: '/v2',
  },
);
